import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, BookOpen, Monitor, Eye, Maximize2, Minimize2, RefreshCw, PanelLeftOpen, PanelLeftClose, PhoneOff } from 'lucide-react'
import { Header } from '../Header'
import { BreadcrumbBar } from '../layout/BreadcrumbBar'
import { Sidebar } from '../Sidebar'
import { TasksSidebar } from './TaskSidebar'
import { LabGuidePdfViewer } from './LabGuideViewer'
import { JumpHostView } from './JumpHostView'
import { LaunchCard } from './LaunchCard'
import { useLabGuide } from '@renderer/hooks/useLabGuide'
import { useUser } from '@renderer/context/UserContext'
import { useLabSession } from '@renderer/hooks/useLabSession'
import { Task } from '@renderer/types/api'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'
import { setCurrentSession, clearCurrentSession } from '@renderer/lib/currentSession'

type MainTab = 'jump-host' | 'lab-guide'
type LabStatus = 'intro' | 'provisioning' | 'ready'

export const LabSessionPage = () => {
  const { exerciseId } = useParams<{ exerciseId: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const { setBreadcrumbs } = useBreadcrumbs()

  const {
    exercise,
    loading,
    error,
    messages,
    inputValue,
    isTyping,
    isSending,
    conversationId,
    sessionState,
    conversations,
    conversationsLoaded,
    setInputValue,
    handleSend,
    handleKeyPress,
    sendMessageWithStreaming,
    startNewConversation,
    loadConversation,
    lkConnected,
    lkConnecting,
    lkTalking,
    lkAgentStatus,
    lkSessionReady,
    lkConnect,
    lkDisconnect,
    lkSendScreenshot,
    lkStartTalking,
    lkStopTalking,
    lkSendJumpToTask,
    lkAssistMode,
    lkSetAssistMode,
    lkSendHelpRequest,
    lkHelpPending,
    lkHelpRejection,
    lkThinking,
    lkUnverifiedTaskIds,
    lkJumpRejection,
  } = useLabSession(exerciseId)

  const {
    pdfUrl,
    pageStart,
    pageEnd,
    moduleTitle,
    courseId,
    moduleId,
    courseName,
    isLoading: labGuideLoading,
    error: labGuideError
  } = useLabGuide(exercise)

  useEffect(() => {
    setCurrentSession({
      sessionId: conversationId,
      courseId,
      moduleId,
      conceptId: exerciseId ?? null
    })
    return () => clearCurrentSession()
  }, [conversationId, courseId, moduleId, exerciseId])

  const [activeTab, setActiveTab] = useState<MainTab>('jump-host')
  const [labStatus, setLabStatus] = useState<LabStatus>('intro')
  const [labGuideToolbar, setLabGuideToolbar] = useState<React.ReactNode>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [rdpRefreshKey, setRdpRefreshKey] = useState(0)
  const [showTasks, setShowTasks] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const mainPanelRef = useRef<HTMLDivElement>(null)

  const [taskClickHint, setTaskClickHint] = useState<string | null>(null)

  const handleSelectTask = async (task: Task) => {
    if (labStatus !== 'ready') {
      setTaskClickHint('Start the lab session first.')
      return
    }
    if (!lkConnected || !lkSessionReady) {
      setTaskClickHint('Hang on — the tutor is still joining.')
      return
    }
    setTaskClickHint(null)
    const ok = await lkSendJumpToTask(task.id)
    if (ok) setActiveTab('jump-host')
  }

  useEffect(() => {
    if (!lkJumpRejection) return
    const why: Record<string, string> = {
      session_not_started: 'The tutor is still getting started.',
      unknown_task: "That task isn't part of this exercise.",
      load_failed: "That task couldn't be loaded — try again."
    }
    setTaskClickHint(why[lkJumpRejection.reason] ?? "That jump didn't go through.")
  }, [lkJumpRejection])

  useEffect(() => {
    if (!taskClickHint) return
    const t = setTimeout(() => setTaskClickHint(null), 4000)
    return () => clearTimeout(t)
  }, [taskClickHint])

  const handleHelp = useCallback(() => {
    void lkSendHelpRequest()
  }, [lkSendHelpRequest])

  const handleFullscreen = () => setIsFullscreen(v => !v)

  const handleEndSession = () => {
    lkDisconnect()
    setLabStatus('intro')
  }

  const [launchMode, setLaunchMode] = useState<'guide' | 'assist'>('guide')

  const handleConnecting = useCallback(() => {
    void lkConnect(launchMode)
  }, [lkConnect, launchMode])

  const handleLaunch = useCallback(() => {
    if (conversations.length > 0) {
      void loadConversation(conversations[0].id)
    }
    setLabStatus('provisioning')
  }, [conversations, loadConversation])

  const handleBack = useCallback(() => navigate(-1), [navigate])
  const handleProvisioned = useCallback(() => setLabStatus('ready'), [])

  useEffect(() => {
    if (!exercise) return
    setBreadcrumbs([
      { label: 'Courses', path: '/main' },
      ...(courseName && courseId ? [{ label: courseName, path: `/course/${courseId}/modules` }] : []),
      ...(moduleTitle && courseId && moduleId ? [{ label: moduleTitle, path: `/course/${courseId}/module/${moduleId}/exercises` }] : []),
      { label: exercise.title }
    ])
  }, [exercise, moduleTitle, courseId, moduleId, courseName, setBreadcrumbs])

  if (loading) {
    return (
      <div className="h-screen flex" style={{ background: '#ffffff', fontFamily: 'Sora, system-ui, sans-serif' }}>
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header userEmail={user?.email} />
          <BreadcrumbBar />
          <main className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7a5a1e' }} />
              <p className="text-sm" style={{ color: '#7a6e54' }}>Loading exercise...</p>
            </div>
          </main>
        </div>
      </div>
    )
  }

  if (error || !exercise) {
    return (
      <div className="h-screen flex" style={{ background: '#ffffff', fontFamily: 'Sora, system-ui, sans-serif' }}>
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header userEmail={user?.email} />
          <BreadcrumbBar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="mb-2" style={{ color: '#a8543a' }}>
                {error ? 'Error loading exercise' : 'Exercise not found'}
              </p>
              {error && <p className="text-sm" style={{ color: '#7a6e54' }}>{error}</p>}
              <button onClick={() => navigate(-1)} className="mt-4 text-sm underline" style={{ color: '#4a3f2a' }}>
                Go back
              </button>
            </div>
          </main>
        </div>
      </div>
    )
  }

  const jumpHostBody = labStatus === 'ready' ? (
    <JumpHostView
      rdpKey={rdpRefreshKey}
      messages={messages}
      isTyping={isTyping}
      isSending={isSending}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSend={handleSend}
      onKeyPress={handleKeyPress}
      lkConnected={lkConnected}
      lkConnecting={lkConnecting}
      lkTalking={lkTalking}
      lkAgentStatus={lkAgentStatus}
      lkSendScreenshot={lkSendScreenshot}
      lkStartTalking={lkStartTalking}
      lkStopTalking={lkStopTalking}
      onEndSession={handleEndSession}
      onNewChat={startNewConversation}
      thinking={lkThinking}
      assistMode={lkAssistMode}
      onHelp={lkConnected && lkSessionReady ? handleHelp : null}
      helpPending={lkHelpPending}
      helpRejection={lkHelpRejection}
    />
  ) : (
    <LaunchCard
      exercise={exercise}
      mode={labStatus === 'provisioning' ? 'provisioning' : 'intro'}
      onLaunch={handleLaunch}
      onBack={handleBack}
      onProvisioned={handleProvisioned}
      onConnecting={handleConnecting}
      launchReady={conversationsLoaded}
      teachingMode={launchMode}
      onTeachingModeChange={setLaunchMode}
    />
  )

  return (
    <div className="h-screen flex" style={{ background: '#ffffff', fontFamily: 'Sora, system-ui, sans-serif' }}>
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header userEmail={user?.email} />
        <BreadcrumbBar />

        <main className="flex-1 flex overflow-hidden p-2 sm:p-3.5">
          <div
            className="flex-1 flex rounded-2xl border overflow-hidden min-w-0"
            style={{ background: '#ffffff', borderColor: '#e4d6b0', boxShadow: '0 8px 32px rgba(74, 63, 42, 0.14), 0 2px 8px rgba(74, 63, 42, 0.08)' }}
          >
            <MainPanel
              activeTab={activeTab}
              onTabChange={setActiveTab}
              labGuideToolbar={labGuideToolbar}
              onFullscreen={handleFullscreen}
              onRefresh={() => {
                if (activeTab === 'jump-host') setRdpRefreshKey((k) => k + 1)
                else setRefreshKey((k) => k + 1)
              }}
              onEndSession={handleEndSession}
              sessionStarted={labStatus === 'ready'}
              showTasks={showTasks}
              onToggleTasks={() => setShowTasks(v => !v)}
              isFullscreen={isFullscreen}
              containerRef={mainPanelRef}
              jumpHostBody={jumpHostBody}
              labGuideBody={
                <LabGuidePdfViewer
                  key={refreshKey}
                  pdfUrl={pdfUrl}
                  pageStart={pageStart}
                  pageEnd={pageEnd}
                  isLoading={labGuideLoading}
                  error={labGuideError}
                  moduleTitle={moduleTitle}
                  exerciseTitle={exercise?.title ?? null}
                  refCode={exercise?.ref_code ?? null}
                  onSendToChat={(displayMessage, fullMessage) => {
                    setInputValue('')
                    sendMessageWithStreaming(fullMessage, null, displayMessage)
                  }}
                  renderExternalToolbar={setLabGuideToolbar}
                />
              }
            />

            {/* Tasks panel — right, collapsible */}
            <div
              className="shrink-0 flex flex-col overflow-hidden"
              style={{
                width: showTasks ? 'clamp(200px, 20vw, 300px)' : 0,
                borderLeft: showTasks ? '1px solid #e4d6b0' : 'none',
                background: activeTab === 'lab-guide' ? '#f5ecd6' : '#ffffff',
                transition: 'width 0.3s ease, background 0.4s ease'
              }}
            >
              <TasksSidebar
                tasks={exercise.tasks || []}
                completedTaskIds={sessionState?.completed_tasks || []}
                unverifiedTaskIds={lkUnverifiedTaskIds}
                currentTaskId={sessionState?.task_id}
                noticeText={taskClickHint}
                teachingMode={lkAssistMode}
                onTeachingModeChange={lkSetAssistMode}
                onSelectTask={handleSelectTask}
                sessionActive={labStatus === 'ready'}
                moduleTitle={moduleTitle ?? undefined}
                exerciseTitle={exercise?.title}
                exerciseRefCode={exercise?.ref_code}
                terminalHint={
                  sessionState?.current_step?.command
                    ? { label: sessionState.current_step.command_label || 'Run command', command: sessionState.current_step.command }
                    : labStatus === 'ready'
                      ? { label: 'Run on cluster3', command: 'ssh admin@192.168.0.103' }
                      : null
                }
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

// --- Main panel ---

interface MainPanelProps {
  activeTab: MainTab
  onTabChange: (t: MainTab) => void
  labGuideToolbar: React.ReactNode
  onFullscreen: () => void
  onRefresh: () => void
  onEndSession: () => void
  showTasks: boolean
  onToggleTasks: () => void
  isFullscreen: boolean
  sessionStarted: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  jumpHostBody: React.ReactNode
  labGuideBody: React.ReactNode
}

const MainPanel: React.FC<MainPanelProps> = ({
  activeTab,
  onTabChange,
  labGuideToolbar,
  onFullscreen,
  onRefresh,
  onEndSession,
  showTasks,
  onToggleTasks,
  isFullscreen,
  sessionStarted,
  containerRef,
  jumpHostBody,
  labGuideBody
}) => {
  return (
    <>
      <style>{`
        @keyframes fs-enter { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div
        ref={containerRef}
        className="flex flex-col min-w-0"
        style={isFullscreen
          ? { position: 'fixed', inset: 0, zIndex: 9999, background: '#ffffff', animation: 'fs-enter 0.2s ease forwards' }
          : { flex: 1, minHeight: 0 }}
      >
        {/* Toolbar */}
        <div
          className="shrink-0 flex items-center gap-1.5 px-2.5 h-10 border-b min-w-0 overflow-hidden"
          style={{ background: '#ffffff', borderColor: '#e4d6b0', color: '#4a3f2a' }}
        >
          <TabButton
            active={activeTab === 'jump-host'}
            onClick={() => onTabChange('jump-host')}
            icon={<Monitor className="w-3.5 h-3.5" />}
            label="Jump Host"
          />
          <TabButton
            active={activeTab === 'lab-guide'}
            onClick={() => onTabChange('lab-guide')}
            icon={<BookOpen className="w-3.5 h-3.5" />}
            label="Lab Guide"
          />

          {activeTab === 'jump-host' && (
            <div className="ml-2 text-[11px] hidden xl:block" style={{ color: '#7a6e54' }}>
              Streamed via secure RDP · 1080p
            </div>
          )}
          {activeTab === 'lab-guide' && labGuideToolbar && (
            <div className="ml-1 flex-1 min-w-0 overflow-hidden">{labGuideToolbar}</div>
          )}

          <div className="flex-1 shrink" />

          {activeTab === 'jump-host' && (
            <div
              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[11px] font-medium"
              style={{ background: 'rgba(200, 154, 58, 0.08)', borderColor: '#f0e2b6', color: '#b8923a' }}
            >
              <Eye className="w-3.5 h-3.5" />
              <span className="hidden 2xl:inline">AI can see your screen</span>
            </div>
          )}

          <IconBtn title={showTasks ? 'Hide tasks' : 'Show tasks'} onClick={onToggleTasks}>
            {showTasks ? <PanelLeftClose className="w-3.5 h-3.5" /> : <PanelLeftOpen className="w-3.5 h-3.5" />}
          </IconBtn>
          <IconBtn title="Refresh" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </IconBtn>
          <IconBtn title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} onClick={onFullscreen}>
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </IconBtn>
          {sessionStarted && (
            <button
              title="End session"
              onClick={onEndSession}
              className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-[11px] font-medium transition-colors shrink-0"
              style={{ background: '#fdf0ed', border: '1px solid #f0c4b5', color: '#a8543a' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f9ddd6' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fdf0ed' }}
            >
              <PhoneOff className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">End Session</span>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden relative" style={{ background: '#ffffff' }}>
          <div
            className="absolute inset-0"
            style={{ display: activeTab === 'jump-host' ? 'flex' : 'none', flexDirection: 'column' }}
          >
            {jumpHostBody}
          </div>
          <div
            className="absolute inset-0"
            style={{ display: activeTab === 'lab-guide' ? 'flex' : 'none', flexDirection: 'column', background: '#f0e8d8' }}
          >
            {labGuideBody}
          </div>
        </div>
      </div>
    </>
  )
}

const TabButton: React.FC<{
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-[12px] font-medium transition-colors whitespace-nowrap shrink-0"
    style={
      active
        ? { background: '#faf4e3', border: '1px solid #e4d6b0', color: '#1d160a' }
        : { background: 'transparent', border: '1px solid transparent', color: '#7a6e54' }
    }
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
  </button>
)

const IconBtn: React.FC<{ title: string; children: React.ReactNode; onClick?: () => void }> = ({
  title,
  children,
  onClick
}) => (
  <button
    title={title}
    onClick={onClick}
    className="w-6 h-6 grid place-items-center rounded-md transition-colors"
    style={{ color: '#4a3f2a', background: '#faf4e3', border: '1px solid #e4d6b0' }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = '#f0e4c4'
      e.currentTarget.style.borderColor = '#c8a870'
      e.currentTarget.style.color = '#1d160a'
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = '#faf4e3'
      e.currentTarget.style.borderColor = '#e4d6b0'
      e.currentTarget.style.color = '#4a3f2a'
    }}
  >
    {children}
  </button>
)
